import { useEffect } from 'react';
import dayjs from 'dayjs';
import type { Photo, PhotoCategory } from '../lib/photos';
import { AuthedImage } from './AuthedImage';
import { formatFileSize } from '../lib/imageCompression';

interface PhotoLightboxProps {
  photo: Photo;
  onClose: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  onDelete?: () => void;
  canDelete?: boolean;
  /** Wenn gesetzt: Dropdown zum Verschieben in andere Kategorie. */
  categories?: PhotoCategory[];
  onMove?: (newCategoryId: number | null) => void;
}

/**
 * Vollbild-Anzeige eines Fotos. Esc schliesst, Pfeile blättern.
 * Schwarzer Hintergrund, Metadaten + Aktionen unten.
 */
export function PhotoLightbox({
  photo,
  onClose,
  onPrev,
  onNext,
  onDelete,
  canDelete,
  categories,
  onMove,
}: PhotoLightboxProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && onPrev) onPrev();
      if (e.key === 'ArrowRight' && onNext) onNext();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose, onPrev, onNext]);

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,.93)',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        // iOS-Safe-Area respektieren — Dynamic Island / Statusbar oben,
        // Home-Indicator unten, Landscape-Notch links/rechts.
        paddingTop: 'calc(env(safe-area-inset-top, 0px) + 20px)',
        paddingRight: 'calc(env(safe-area-inset-right, 0px) + 20px)',
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 20px)',
        paddingLeft: 'calc(env(safe-area-inset-left, 0px) + 20px)',
      }}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Schliessen"
        style={{
          position: 'absolute',
          top: 'calc(env(safe-area-inset-top, 0px) + 16px)',
          right: 'calc(env(safe-area-inset-right, 0px) + 16px)',
          background: 'rgba(0,0,0,.5)',
          color: '#fff',
          border: '1px solid rgba(255,255,255,.3)',
          borderRadius: 8,
          padding: '8px 14px',
          cursor: 'pointer',
          fontSize: 18,
        }}
      >
        ×
      </button>

      {onPrev && (
        <button
          type="button"
          onClick={onPrev}
          aria-label="Vorheriges Foto"
          style={{
            position: 'absolute',
            left: 'calc(env(safe-area-inset-left, 0px) + 16px)',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'rgba(0,0,0,.5)',
            color: '#fff',
            border: '1px solid rgba(255,255,255,.3)',
            borderRadius: '50%',
            width: 44,
            height: 44,
            cursor: 'pointer',
            fontSize: 22,
          }}
        >
          ‹
        </button>
      )}
      {onNext && (
        <button
          type="button"
          onClick={onNext}
          aria-label="Nächstes Foto"
          style={{
            position: 'absolute',
            right: 'calc(env(safe-area-inset-right, 0px) + 16px)',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'rgba(0,0,0,.5)',
            color: '#fff',
            border: '1px solid rgba(255,255,255,.3)',
            borderRadius: '50%',
            width: 44,
            height: 44,
            cursor: 'pointer',
            fontSize: 22,
          }}
        >
          ›
        </button>
      )}

      <AuthedImage
        photoId={photo.id}
        alt={photo.caption ?? photo.original_name ?? `Foto ${photo.id}`}
        eager
        fit="contain"
        style={{
          width: '90vw',
          height: '78vh',
        }}
      />

      <div
        style={{
          color: '#fff',
          marginTop: 16,
          textAlign: 'center',
          fontSize: 13,
          maxWidth: '90vw',
        }}
      >
        {photo.caption && (
          <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 6 }}>{photo.caption}</div>
        )}
        <div style={{ color: 'rgba(255,255,255,.7)', display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
          {photo.category_name && <span>📁 {photo.category_name}</span>}
          <span>📅 {dayjs(photo.uploaded_at).format('DD.MM.YYYY HH:mm')}</span>
          {photo.uploader_abbr && <span>👤 {photo.uploader_abbr}</span>}
          {photo.width && photo.height && <span>📐 {photo.width}×{photo.height}</span>}
          <span>💾 {formatFileSize(photo.size_bytes)}</span>
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', alignItems: 'center', marginTop: 14, flexWrap: 'wrap' }}>
          {onMove && categories && (
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                color: '#fff',
                fontSize: 13,
                background: 'rgba(255,255,255,.1)',
                border: '1px solid rgba(255,255,255,.25)',
                borderRadius: 6,
                padding: '4px 8px',
              }}
            >
              <span style={{ color: 'rgba(255,255,255,.7)' }}>Kategorie:</span>
              <select
                value={photo.category_id ?? 'null'}
                onChange={(e) => {
                  const v = e.target.value;
                  onMove(v === 'null' ? null : Number(v));
                }}
                style={{
                  background: 'transparent',
                  color: '#fff',
                  border: 'none',
                  fontSize: 13,
                  outline: 'none',
                }}
              >
                <option value="null" style={{ background: '#222' }}>— Ohne —</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id} style={{ background: '#222' }}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          {canDelete && onDelete && (
            <button type="button" onClick={onDelete} className="btn btn-danger btn-sm">
              Löschen
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
